export type Vector3 = {
    x: number;
    y: number;
    z: number;
};

export type NormalizedColor = {
    r: number;
    g: number;
    b: number;
    a?: number;
};

export type HTMLColor = string;

// "None" is stored as null throughout: a merge can't delete a key.
export type Material = {
    color?: NormalizedColor | HTMLColor;
    texture?: string;
    alpha?: number;
    emissive?: NormalizedColor | HTMLColor | null;
    specular?: NormalizedColor | HTMLColor | null;
    wireframe?: boolean;
};

export type AnimationKind = 'spin' | 'bob' | 'sway' | 'pulse';

export type Axis = 'x' | 'y' | 'z';

export type SceneAnimation = {
    kind: AnimationKind;
    axis?: Axis | null;
    speed?: number | null;
    amount?: number | null;
};

export type CameraMode = 'fly' | 'orbit';

export type SceneCamera = {
    position?: Vector3;
    target?: Vector3;
    fov?: number;
    mode?: CameraMode;
};

export type SceneFog = {
    color?: NormalizedColor | HTMLColor;
    density?: number;
};

export type SceneDefaultLight = {
    on?: boolean;
    intensity?: number;
};

export type SceneSettings = {
    background?: NormalizedColor | HTMLColor | null;
    camera?: SceneCamera | null;
    fog?: SceneFog | null;
    defaultLight?: SceneDefaultLight | null;
};

export type SceneEntity = {
    elementId: string;
    type: string;
    name?: string | null;
    parent?: string | null;
    position?: Vector3;
    rotation?: Vector3;
    scaling?: Vector3;
    material?: Material;
    animation?: SceneAnimation | null;
} & (
    | {type: 'sphere'; diameter: number; segments?: number | null}
    | {
          type: 'box';
          size?: number;
          width?: number | null;
          height?: number | null;
          depth?: number | null;
      }
    | {type: 'torus'; diameter: number; thickness: number}
    | {
          type: 'cylinder';
          height: number;
          diameter: number;
          diameterTop?: number | null;
          diameterBottom?: number | null;
          tessellation?: number | null;
      }
    | {type: 'plane'; width: number; height: number; doubleSided?: boolean}
    | {type: 'disc'; radius: number; tessellation?: number | null}
    | {type: 'icosphere'; radius: number; subdivisions: number}
    | {type: 'model'; url: string}
    | {
          type: 'pointLight';
          intensity?: number;
          color?: NormalizedColor | HTMLColor;
      }
    | {
          type: 'directionalLight';
          direction: Vector3;
          intensity?: number;
          color?: NormalizedColor | HTMLColor;
      }
    | {
          type: 'spotLight';
          direction: Vector3;
          angle?: number;
          exponent?: number;
          intensity?: number;
          color?: NormalizedColor | HTMLColor;
      }
    | {
          type: 'hemisphericLight';
          direction: Vector3;
          intensity?: number;
          color?: NormalizedColor | HTMLColor;
          groundColor?: NormalizedColor | HTMLColor;
      }
    | {
          type: 'torusKnot';
          radius: number;
          tube: number;
          radialSegments: number;
          tubularSegments: number;
          p: number;
          q: number;
      }
    | {
          type: 'ground';
          width: number;
          height: number;
          subdivisions?: number;
          heightMap?: string;
          maxHeight?: number | null;
      }
    | {
          type: 'lines';
          points: Vector3[];
          colors?: (NormalizedColor | HTMLColor)[];
      }
    | {
          type: 'dashedLines';
          points: Vector3[];
          dashSize: number;
          gapSize: number;
          dashNb?: number;
          colors?: (NormalizedColor | HTMLColor)[];
      }
    | {
          type: 'tube';
          path: Vector3[];
          radius?: number | null;
          tessellation?: number | null;
          cap?: number | null;
      }
    | {
          // Old rows may hold the twist as a numeric `rotation` instead.
          type: 'extrudeShape';
          shape: Vector3[];
          path: Vector3[];
          scale?: number;
          twist?: number | null;
          cap?: number;
      }
    | {type: 'lathe'; shape: Vector3[]; segments?: number; cap?: number}
    | {
          type: 'text';
          text: string;
          color?: NormalizedColor | HTMLColor;
          height?: number;
          billboard?: boolean;
          background?: NormalizedColor | HTMLColor | null;
      }
    | {type: 'group'}
    | ({type: 'sceneSettings'} & SceneSettings)
);

export const SceneEvents = {
    EntityAdd: 'entity:add',
    EntityModify: 'entity:modify',
    EntityDelete: 'entity:delete'
};

export type SceneData = Record<string, SceneEntity>;
